import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../providers/AuthContext";
import { getLogs } from "../../api/Telemetry";
import toast from "react-hot-toast";

import LogTable, { mapHubLogToRow } from "../../components/logs/LogsTable";
import { useTranslation } from "react-i18next";

const ALL_LOG_TYPES = [
    "Status",
    "Authorization",
    "Transaction",
    "Bootnotification",
    "Heartbeat",
    "Charging-Command",
    "Change-Request",
    "Booking-Request",
    "Charge-Attempt",
];

const DEFAULT_SELECTED_LOG_TYPES = [
    "Status",
    "Authorization",
    "Transaction",
    "Bootnotification",
    "Charging-Command",
    "Change-Request",
    "Booking-Request",
    "Charge-Attempt",
];

const UI_TO_API_LOG_TYPES = {
    Status: ["ChargingPointStatus", "ChargingConnectorStatus", "NotifyEvent"],
    Authorization: ["Authorization"],
    Transaction: ["TransactionStatus"],
    Bootnotification: ["BootNotification"],
    Heartbeat: ["Heartbeat"],
    "Charging-Command": ["ChargingCommand"],
    "Change-Request": ["ChangeRequest"],
    "Booking-Request": ["BookingRequest"],
    "Charge-Attempt": ["ChargeAttempt"],
};

const ChargingSiteStatusLog = () => {
    const { t } = useTranslation();
    const { id: sitePublicId } = useParams();
    const { getAccessToken } = useAuth();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [search, setSearch] = useState("");

    const [pagination, setPagination] = useState({
        totalCount: 0,
        page: 1,
        pageSize: 100,
        totalPages: 1,
    });

    const [selectedLogTypes, setSelectedLogTypes] = useState(
        DEFAULT_SELECTED_LOG_TYPES
    );

    const normalizedSearch = search.trim();
    const canSearch =
        normalizedSearch.length === 0 || normalizedSearch.length >= 3;

    const buildBaseRequest = useCallback(
        (extra = {}) => {
            const request = {
                chargingSitePublicIds: sitePublicId ? [sitePublicId] : [],
                page,
                pageSize,
                sortColumn: "createdDateTimeUtc",
                sortOrder: "desc",
                ...((normalizedSearch.length === 0 ||
                    normalizedSearch.length >= 3) &&
                    normalizedSearch
                    ? { search: normalizedSearch }
                    : {}),
                ...extra,
            };

            return request;
        },
        [sitePublicId, page, pageSize, normalizedSearch]
    );

    const fetchLogs = useCallback(
        async ({ extraRequest = {}, selectedTypesOverride = null } = {}) => {
            const token = await getAccessToken();

            const effectiveSelectedTypes =
                selectedTypesOverride ?? selectedLogTypes;

            const apiLogTypes = Array.from(
                new Set(
                    effectiveSelectedTypes.flatMap(
                        (type) => UI_TO_API_LOG_TYPES[type] ?? []
                    )
                )
            );

            const request = buildBaseRequest({
                ...extraRequest,
                ...(apiLogTypes.length ? { logTypes: apiLogTypes } : {}),
            });

            const response = await getLogs(token, request);
            const rawLogs = response?.logs ?? response?.items ?? [];

            setPagination({
                totalCount: response?.totalCount ?? 0,
                page: response?.page ?? page,
                pageSize: response?.pageSize ?? pageSize,
                totalPages: response?.totalPages ?? 1,
            });

            return rawLogs
                .map(mapHubLogToRow)
                .filter(Boolean)
                .sort((a, b) => (b.dt || 0) - (a.dt || 0));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
        [getAccessToken, buildBaseRequest, page, pageSize]
    );

    const handleToggleLogType = useCallback(
        (type) => {
            setPage(1);

            const exists = selectedLogTypes.includes(type);
            const next = exists
                ? selectedLogTypes.filter((x) => x !== type)
                : [...selectedLogTypes, type];

            setSelectedLogTypes(next);
            loadLogs(next);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
        [selectedLogTypes]
    );

    const loadLogs = useCallback(
        async (typesOverride = null) => {
            try {
                setLoading(true);
                const mapped = await fetchLogs({
                    selectedTypesOverride: typesOverride,
                });
                setRows(mapped);
            } catch (e) {
                console.error(e);
                toast.error(t("toast.failedFetch", { entity: t("entity.siteLogs") }));
            } finally {
                setLoading(false);
            }
        },
        [fetchLogs]
    );

    const handlePageChange = useCallback((nextPage) => {
        setPage(nextPage);
    }, []);

    const handlePageSizeChange = useCallback((nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
    }, []);

    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        setPage(1);
    }, []);

    useEffect(() => {
        if (!sitePublicId || !canSearch) return;

        let mounted = true;

        const init = async () => {
            try {
                setLoading(true);

                const mapped = await fetchLogs();

                if (!mounted) return;

                setRows(mapped);
            } catch (e) {
                console.error(e);
                toast.error(t("toast.failedFetch", { entity: t("entity.siteLogs") }));
            } finally {
                if (mounted) setLoading(false);
            }
        };

        init();

        return () => {
            mounted = false;
        };
    }, [sitePublicId, fetchLogs, canSearch]);

    return (
        <LogTable
            rows={rows}
            loading={loading}
            pagination={pagination}
            allLogTypes={ALL_LOG_TYPES}
            selectedLogTypes={selectedLogTypes}
            onToggleLogType={handleToggleLogType}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            search={search}
            onSearchChange={handleSearchChange}
            minSearchLength={3}
        />
    );
};

export default ChargingSiteStatusLog;